import { useState, useEffect } from 'react';
import {
  UserEntity,
  useFetchManyUsersQuery,
  useLazyFetchUserProfileQuery,
} from 'src/store/userApi';
import { useTestMatchMutation } from 'src/store/matchTestApi';
import {
  subscribeEvent,
  SocketEvent,
  unsubscribeEvent,
} from 'src/store/socket';
import { PageHead } from 'src/ui/patterns/PageHead';
import { Panel } from 'src/ui/Panel';
import { Modal } from 'src/ui/Modal';
import { Button } from 'src/ui/Button';
import { Badge, StatusBadge } from 'src/ui/Badge';
import { Avatar, GenderIcon } from 'src/ui/Avatar';
import { Icon } from 'src/ui/Icon';
import { SearchInput } from 'src/ui/SearchInput';
import { EmptyState } from 'src/ui/EmptyState';
import { Spinner } from 'src/ui/Spinner';
import { Switch } from 'src/ui/Switch';
import { toPanelUser } from 'src/ui/patterns/userAdapter';

interface MatchEntry {
  matchRule?: { name: string; weight?: number };
  res?: {
    scoreA?: number;
    scoreB?: number;
    weightA?: number;
    weightB?: number;
    prompt?: string;
    promt?: string;
    [key: string]: any;
  };
  scoreA?: number;
  scoreB?: number;
}

/* ---- Small photo thumbnail ---- */
function PhotoThumb({ user, size = 56 }: { user: UserEntity; size?: number }) {
  const photo = user.photos?.[0];
  const url = photo?.lImage?.url || photo?.mImage?.url || photo?.sImage?.url;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-md)',
        flexShrink: 0,
        overflow: 'hidden',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
        background: 'var(--bg-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={user.profile?.name || user.email}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Icon
          name="user"
          size={Math.round(size * 0.38)}
          style={{ color: 'var(--text-muted)' }}
        />
      )}
    </div>
  );
}

/* ---- Candidate slot ---- */
function CandidateSlot({
  user,
  onChoose,
  onClear,
}: {
  user: UserEntity | null;
  onChoose: () => void;
  onClear: () => void;
}) {
  if (!user) {
    return (
      <button
        onClick={onChoose}
        style={{
          width: '100%',
          minHeight: 80,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 12,
          background: 'var(--bg-subtle)',
          border: '1.5px dashed var(--border-strong)',
          borderRadius: 'var(--radius-lg)',
          color: 'var(--text-secondary)',
          transition: 'border-color .14s, background .14s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'var(--accent)';
          (e.currentTarget as HTMLButtonElement).style.background =
            'var(--bg-muted)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'var(--border-strong)';
          (e.currentTarget as HTMLButtonElement).style.background =
            'var(--bg-subtle)';
        }}
      >
        <span
          style={{
            width: 56,
            height: 56,
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-muted)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
          }}
        >
          <Icon name="user" size={22} />
        </span>
        <span className="col" style={{ gap: 2 }}>
          <span
            className="row gap-2"
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-md)',
              color: 'var(--text)',
            }}
          >
            <Icon name="plus" size={15} />
            Choose a person
          </span>
          <span
            style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}
          >
            Search by name, email or location
          </span>
        </span>
      </button>
    );
  }

  const panelUser = toPanelUser(user);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        minHeight: 80,
        background: 'var(--bg-canvas)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 12,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <PhotoThumb user={user} />
      <div className="grow" style={{ minWidth: 0, paddingRight: 56 }}>
        <div className="row gap-2" style={{ marginBottom: 2 }}>
          <span
            className="truncate"
            style={{ fontWeight: 600, fontSize: 'var(--fs-md)', minWidth: 0 }}
          >
            {panelUser.name}
          </span>
          <GenderIcon gender={panelUser.gender as any} size={14} />
        </div>
        <div className="col" style={{ gap: 2, minWidth: 0 }}>
          <div
            className="row gap-1 text-muted"
            style={{ fontSize: 'var(--fs-xs)', minWidth: 0 }}
          >
            {panelUser.age != null && (
              <span
                style={{
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  flexShrink: 0,
                }}
              >
                {panelUser.age}
              </span>
            )}
            {panelUser.age != null && panelUser.loc && (
              <span style={{ color: 'var(--text-faint)', flexShrink: 0 }}>
                &middot;
              </span>
            )}
            {panelUser.loc && (
              <>
                <Icon name="map-pin" size={12} style={{ flexShrink: 0 }} />
                <span className="truncate" style={{ minWidth: 0 }}>
                  {panelUser.loc}
                </span>
              </>
            )}
          </div>
          {panelUser.online && (
            <span
              className="row gap-1"
              style={{ color: 'var(--green-text)', fontSize: 'var(--fs-xs)' }}
            >
              <span className="live-dot" />
              Online
            </span>
          )}
        </div>
      </div>
      <div
        className="row gap-1"
        style={{ position: 'absolute', top: 8, right: 8 }}
      >
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onChoose}
          title="Change person"
        >
          <Icon name="pencil" size={14} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClear}
          title="Remove"
        >
          <Icon name="x" size={15} />
        </Button>
      </div>
    </div>
  );
}

/* ---- Person picker modal ---- */
function PersonPicker({
  open,
  onClose,
  onPick,
  excludeId,
  currentId,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (user: UserEntity) => void;
  excludeId?: string | null;
  currentId?: string | null;
}) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data: usersData, isLoading } = useFetchManyUsersQuery({
    limit: 100,
    search: debouncedQ.trim() || undefined,
  });

  const candidates = (usersData?.data ?? []).filter(
    (u) => !u.deletedAt && u.id !== excludeId
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose a person"
      subtitle="Search and select someone to compare."
      width={460}
    >
      <div className="col gap-3" style={{ paddingBottom: 8 }}>
        <SearchInput
          value={q}
          onChange={setQ}
          debounce={0}
          placeholder="Search name, email or location…"
          autoFocus
        />
        <div
          className="scroll-y"
          style={{ maxHeight: 360, margin: '0 -4px', padding: '0 4px' }}
        >
          {isLoading ? (
            <div
              style={{ display: 'flex', justifyContent: 'center', padding: 24 }}
            >
              <Spinner />
            </div>
          ) : candidates.length === 0 ? (
            <EmptyState
              icon="search"
              title="No users found"
              desc="Try a different name or location."
            />
          ) : (
            candidates.map((u) => {
              const pu = toPanelUser(u);
              const isSelected = u.id === currentId;
              return (
                <div
                  key={u.id}
                  role="button"
                  tabIndex={0}
                  className={'list-row' + (isSelected ? ' selected' : '')}
                  onClick={() => {
                    onPick(u);
                    onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onPick(u);
                      onClose();
                    }
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <Avatar
                      name={pu.name}
                      gender={pu.gender as any}
                      ring
                      hue={pu.hue}
                      size={36}
                    />
                    {pu.online && (
                      <span
                        style={{
                          position: 'absolute',
                          right: -1,
                          bottom: -1,
                          width: 10,
                          height: 10,
                          borderRadius: 99,
                          background: 'var(--green-solid)',
                          border: '2px solid var(--bg-canvas)',
                        }}
                      />
                    )}
                  </div>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="row gap-2">
                      <span
                        className="truncate"
                        style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}
                      >
                        {pu.name}
                      </span>
                      <GenderIcon gender={pu.gender as any} size={12} />
                    </div>
                    <div
                      className="text-muted truncate"
                      style={{ fontSize: 'var(--fs-xs)' }}
                    >
                      {[pu.age, pu.loc].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {isSelected ? (
                    <Badge color="blue" size="sm">
                      Selected
                    </Badge>
                  ) : (
                    <Icon
                      name="chevron-right"
                      size={16}
                      style={{ color: 'var(--text-faint)' }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ---- Comparison config ---- */
const COMPARE_ROWS = [
  {
    key: 'loc',
    label: 'Location',
    icon: 'map-pin' as const,
    weight: 25,
    get: (u: UserEntity) =>
      [u.profile?.city, u.profile?.state, u.profile?.country]
        .filter(Boolean)
        .join(', ') || '—',
    match: (a: UserEntity, b: UserEntity) =>
      !!(a.profile?.country && a.profile?.country === b.profile?.country),
  },
  {
    key: 'age',
    label: 'Age',
    icon: 'calendar' as const,
    weight: 20,
    get: (u: UserEntity) =>
      u.profile?.age != null ? String(u.profile.age) : '—',
    match: (a: UserEntity, b: UserEntity) =>
      !!(
        a.profile?.age != null &&
        b.profile?.age != null &&
        Math.abs(a.profile.age - b.profile.age) <= 6
      ),
  },
  {
    key: 'looking',
    label: 'Looking for',
    icon: 'heart' as const,
    weight: 25,
    get: (u: UserEntity) =>
      (u.profile?.answers as any)?.looking ||
      (u.profile as any)?.looking ||
      '—',
    match: (a: UserEntity, b: UserEntity) => {
      const va =
        (a.profile?.answers as any)?.looking || (a.profile as any)?.looking;
      const vb =
        (b.profile?.answers as any)?.looking || (b.profile as any)?.looking;
      return !!(va && va === vb);
    },
  },
  {
    key: 'dietary',
    label: 'Dietary',
    icon: 'flag' as const,
    weight: 10,
    get: (u: UserEntity) =>
      (u.profile?.answers as any)?.dietary ||
      (u.profile as any)?.dietary ||
      '—',
    match: (a: UserEntity, b: UserEntity) => {
      const va =
        (a.profile?.answers as any)?.dietary || (a.profile as any)?.dietary;
      const vb =
        (b.profile?.answers as any)?.dietary || (b.profile as any)?.dietary;
      return !!(va && va === vb);
    },
  },
  {
    key: 'occupation',
    label: 'Occupation',
    icon: 'user' as const,
    weight: 0,
    get: (u: UserEntity) =>
      (u.profile?.answers as any)?.occupation ||
      (u.profile as any)?.occupation ||
      '—',
    match: null,
  },
  {
    key: 'languages',
    label: 'Languages',
    icon: 'languages' as const,
    weight: 0,
    get: (u: UserEntity) =>
      (u.profile?.answers as any)?.languages ||
      (u.profile as any)?.languages ||
      '—',
    match: null,
  },
];

const GRID = '124px minmax(0,1fr) minmax(0,1fr)';

function ValueCell({ value, matched }: { value: string; matched: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 'var(--fs-sm)',
        fontWeight: matched ? 600 : 500,
        padding: '6px 9px',
        borderRadius: 'var(--radius-sm)',
        background: matched ? 'var(--green-bg)' : 'transparent',
        color: matched ? 'var(--green-text)' : 'var(--text)',
        border:
          '1px solid ' + (matched ? 'var(--green-border)' : 'transparent'),
        minHeight: 30,
      }}
    >
      {matched && (
        <Icon
          name="check"
          size={13}
          strokeWidth={3}
          style={{ flexShrink: 0 }}
        />
      )}
      <span style={{ minWidth: 0 }}>{value}</span>
    </div>
  );
}

/* ---- Comparison panel ---- */
function ComparePanel({
  userA,
  userB,
  onChoose,
  onClear,
  onSwap,
}: {
  userA: UserEntity | null;
  userB: UserEntity | null;
  onChoose: (slot: 0 | 1) => void;
  onClear: (slot: 0 | 1) => void;
  onSwap: () => void;
}) {
  const both = userA && userB;
  const right = both ? (
    <Button
      variant="ghost"
      size="sm"
      leftIcon="refresh"
      onClick={onSwap}
      title="Swap people"
    >
      Swap
    </Button>
  ) : undefined;

  return (
    <Panel
      className="grow"
      title="Comparison"
      icon="users"
      headerRight={right}
      scroll
    >
      <div className="col gap-3">
        {/* Candidate selectors */}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 12,
              alignItems: 'stretch',
            }}
          >
            <div />
            <CandidateSlot
              user={userA}
              onChoose={() => onChoose(0)}
              onClear={() => onClear(0)}
            />
            <CandidateSlot
              user={userB}
              onChoose={() => onChoose(1)}
              onClear={() => onClear(1)}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 'calc(50% + 68px)',
              transform: 'translate(-50%, -50%)',
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: 'var(--bg-canvas)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--fs-2xs)',
              fontWeight: 700,
              color: 'var(--text-muted)',
              letterSpacing: '0.04em',
            }}
          >
            VS
          </div>
        </div>

        {/* Attributes (only when both chosen) */}
        {both ? (
          <div>
            <div className="eyebrow" style={{ marginBottom: 2 }}>
              Attributes
            </div>
            {COMPARE_ROWS.map((row) => {
              const matched = row.match ? row.match(userA, userB) : false;
              return (
                <div
                  key={row.key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: 12,
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div className="row gap-2" style={{ minWidth: 0 }}>
                    <Icon
                      name={row.icon}
                      size={13}
                      style={{ color: 'var(--text-muted)', flexShrink: 0 }}
                    />
                    <span
                      className="truncate"
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-secondary)',
                        fontWeight: 500,
                      }}
                    >
                      {row.label}
                    </span>
                    {row.weight > 0 && (
                      <span
                        className="mono"
                        style={{
                          fontSize: 'var(--fs-2xs)',
                          color: 'var(--text-faint)',
                        }}
                      >
                        {row.weight}%
                      </span>
                    )}
                  </div>
                  <ValueCell value={row.get(userA)} matched={matched} />
                  <ValueCell value={row.get(userB)} matched={matched} />
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              padding: '30px 16px',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            {!userA && !userB
              ? 'Choose two people above to compare them across the matching rules.'
              : 'Choose one more person to start the comparison.'}
          </div>
        )}
      </div>
    </Panel>
  );
}

/* ---- Score ring ---- */
function ScoreRing({ value, pass }: { value: number; pass: boolean }) {
  const color = pass ? 'var(--green-solid)' : 'var(--accent)';
  return (
    <div style={{ position: 'relative', width: 130, height: 130 }}>
      <div
        style={{
          width: 130,
          height: 130,
          borderRadius: '50%',
          background: `conic-gradient(${color} ${
            value * 3.6
          }deg, var(--bg-muted) 0)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 11,
          borderRadius: '50%',
          background: 'var(--bg-canvas)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 36,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: pass ? 'var(--green-text)' : 'var(--accent-text)',
          }}
        >
          {value}
          <span style={{ fontSize: 16 }}>%</span>
        </span>
        <span className="eyebrow" style={{ fontSize: 'var(--fs-2xs)' }}>
          Compatibility
        </span>
      </div>
    </div>
  );
}

/* ---- Breakdown row (from real socket ADMIN_LOG entries) ---- */
function BreakdownRow({ entry, index }: { entry: MatchEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const res = entry.res || {};
  const scoreA = res.scoreA ?? 0;
  const scoreB = res.scoreB ?? 0;
  const avgScore = Math.round((scoreA + scoreB) / 2);
  const pass = avgScore >= 60;
  const barColor =
    avgScore >= 70
      ? 'var(--green-solid)'
      : avgScore >= 50
      ? 'var(--amber-solid)'
      : 'var(--red-solid)';

  return (
    <div style={{ padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
      <div
        className="row"
        style={{
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 7,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v);
        }}
      >
        <span className="row gap-2" style={{ minWidth: 0 }}>
          <span
            className="truncate"
            style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
          >
            {index + 1}. {entry.matchRule?.name}
          </span>
        </span>
        <span className="row gap-2" style={{ flexShrink: 0 }}>
          <Badge color="blue" size="sm">
            A: {scoreA}
            {res.weightA != null ? `(${res.weightA})` : ''}
          </Badge>
          <Badge color="green" size="sm">
            B: {scoreB}
            {res.weightB != null ? `(${res.weightB})` : ''}
          </Badge>
          <StatusBadge status={pass ? 'pass' : 'fail'} size="sm" />
          <Icon
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={14}
            style={{ color: 'var(--text-muted)' }}
          />
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 99,
          background: 'var(--bg-muted)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: avgScore + '%',
            borderRadius: 99,
            background: barColor,
            transition: 'width .5s cubic-bezier(.2,.7,.3,1)',
          }}
        />
      </div>
      {expanded && (
        <div className="col gap-2" style={{ marginTop: 10 }}>
          {Object.entries(res).map(([key, value]) => {
            if (key === 'prompt' || key === 'promt') return null;
            return (
              <div key={key}>
                <div
                  style={{
                    fontSize: 'var(--fs-2xs)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 600,
                    marginBottom: 2,
                  }}
                >
                  {key}
                </div>
                <div
                  style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {String(value)}
                </div>
              </div>
            );
          })}
          {(res.promt || res.prompt) && (
            <div>
              <div
                style={{
                  fontSize: 'var(--fs-2xs)',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Prompt
              </div>
              <div
                style={{
                  background: 'var(--bg-subtle)',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--fs-sm)',
                  whiteSpace: 'pre-wrap',
                  color: 'var(--text-secondary)',
                }}
              >
                {String(res.promt || res.prompt)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---- Result panel ---- */
function ResultPanel({
  userA,
  userB,
}: {
  userA: UserEntity | null;
  userB: UserEntity | null;
}) {
  const [saveMatch, setSaveMatch] = useState(false);
  const [matchEntries, setMatchEntries] = useState<MatchEntry[]>([]);
  const [testMatch, testMatchStatus] = useTestMatchMutation();

  useEffect(() => {
    const onNewAdminLog = (matchInfo: { data: any }) => {
      setMatchEntries((prev) => [...prev, matchInfo.data]);
    };
    subscribeEvent(SocketEvent.ADMIN_LOG, onNewAdminLog);
    return () => {
      unsubscribeEvent(SocketEvent.ADMIN_LOG, onNewAdminLog);
    };
  }, []);

  useEffect(() => {
    setMatchEntries([]);
  }, [userA, userB]);

  const onTest = () => {
    if (!userA || !userB) return;
    setMatchEntries([]);
    testMatch({ userAId: userA.id, userBId: userB.id, saveMatch });
  };

  // Separate score node from breakdown entries
  const ruleEntries = matchEntries.filter((e) => e.matchRule);
  const scoreEntry = matchEntries.find(
    (e) => !e.matchRule && (e.scoreA != null || e.scoreB != null)
  );
  const hasResults = matchEntries.length > 0;

  const scoreA = scoreEntry?.scoreA ?? 0;
  const scoreB = scoreEntry?.scoreB ?? 0;
  const avgTotal = scoreEntry ? Math.round((scoreA + scoreB) / 2) : 0;
  const pass = avgTotal >= 65;

  const nameA = userA
    ? (userA.profile?.name || userA.email)?.split(' ')[0]
    : '';
  const nameB = userB
    ? (userB.profile?.name || userB.email)?.split(' ')[0]
    : '';

  if (!userA || !userB) {
    return (
      <Panel className="grow" title="Match Result" icon="target">
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmptyState
            icon="target"
            title="Run a test"
            desc="Choose two people, then run the match test to see a weighted compatibility score."
          />
        </div>
      </Panel>
    );
  }

  const footer = (
    <div className="col gap-2">
      <label
        className="row gap-2"
        style={{ fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
      >
        <Switch size="sm" checked={saveMatch} onChange={setSaveMatch} />
        Save this result as a match suggestion
      </label>
      <Button
        variant="primary"
        className="btn-block"
        leftIcon={hasResults ? 'refresh' : 'play'}
        onClick={onTest}
        loading={testMatchStatus.isLoading}
      >
        {hasResults ? 'Run again' : 'Run match test'}
      </Button>
    </div>
  );

  return (
    <Panel
      className="grow"
      title="Match Result"
      icon="target"
      scroll
      headerRight={
        hasResults && scoreEntry ? (
          <StatusBadge status={pass ? 'pass' : 'fail'} />
        ) : undefined
      }
      footer={footer}
    >
      {testMatchStatus.isLoading && !hasResults ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner />
        </div>
      ) : !hasResults ? (
        <div className="col gap-3">
          <p
            className="text-secondary"
            style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.55 }}
          >
            {nameA} &amp; {nameB} will be scored across the active match rules,
            combined into one weighted compatibility score.
          </p>
        </div>
      ) : (
        <div className="col gap-3">
          {/* Score ring — show when we have final scores */}
          {scoreEntry && (
            <div
              className="col"
              style={{ alignItems: 'center', gap: 10, padding: '4px 0 8px' }}
            >
              <ScoreRing value={avgTotal} pass={pass} />
              <div className="row gap-3" style={{ justifyContent: 'center' }}>
                <Badge color="blue">A: {scoreA}</Badge>
                <Badge color="green">B: {scoreB}</Badge>
              </div>
              <div
                className="text-secondary"
                style={{ fontSize: 'var(--fs-sm)', textAlign: 'center' }}
              >
                {nameA} &amp; {nameB}
              </div>
            </div>
          )}

          {/* Rule breakdown */}
          {ruleEntries.length > 0 && (
            <div>
              <div className="eyebrow" style={{ marginBottom: 2 }}>
                Rule breakdown
              </div>
              {ruleEntries.map((entry, i) => (
                <BreakdownRow key={i} entry={entry} index={i} />
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

/* ---- Page ---- */
const MatchTest = () => {
  const [userA, setUserA] = useState<UserEntity | null>(null);
  const [userB, setUserB] = useState<UserEntity | null>(null);
  const [pickerFor, setPickerFor] = useState<0 | 1 | null>(null);

  const [fetchUserProfile] = useLazyFetchUserProfileQuery();

  const pickUser = async (u: UserEntity) => {
    // Fetch full profile to ensure we have photos + answers
    try {
      const full = await fetchUserProfile(u.id).unwrap();
      if (pickerFor === 0) setUserA(full);
      else if (pickerFor === 1) setUserB(full);
    } catch {
      // Fall back to the entity from the list
      if (pickerFor === 0) setUserA(u);
      else if (pickerFor === 1) setUserB(u);
    }
  };

  const clearSlot = (slot: 0 | 1) => {
    if (slot === 0) setUserA(null);
    else setUserB(null);
  };

  const swap = () => {
    setUserA(userB);
    setUserB(userA);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <PageHead
        title="Match Test"
        subtitle="Compare two users across the active match rules"
      />

      <div
        style={{
          flex: 1,
          padding: 16,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) 380px',
          gap: 16,
        }}
      >
        <div style={{ overflow: 'hidden', display: 'flex' }}>
          <ComparePanel
            userA={userA}
            userB={userB}
            onChoose={setPickerFor}
            onClear={clearSlot}
            onSwap={swap}
          />
        </div>
        <div style={{ overflow: 'hidden', display: 'flex' }}>
          <ResultPanel
            userA={userA}
            userB={userB}
            key={(userA?.id || '') + '|' + (userB?.id || '')}
          />
        </div>
      </div>

      <PersonPicker
        open={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        excludeId={
          pickerFor !== null ? (pickerFor === 0 ? userB?.id : userA?.id) : null
        }
        currentId={
          pickerFor !== null ? (pickerFor === 0 ? userA?.id : userB?.id) : null
        }
        onPick={pickUser}
      />
    </div>
  );
};

export default MatchTest;
